package gr.tech.site.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.NavContactHeaderTextStyle
import gr.tech.site.styles.NavLinkHoverStyle
import gr.tech.site.styles.Theme
import gr.tech.site.styles.wideCappedWidth
import gr.tech.site.data.allStates
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

data class NavDropdownItem(val label: String, val path: String, val isDivider: Boolean = false) {
    companion object {
        val Divider = NavDropdownItem(label = "", path = "", isDivider = true)
    }
}

// Note: When the user closes the side menu, we don't immediately stop rendering it (at which point it would disappear
// abruptly). Instead, we start animating it out and only stop rendering it when the animation is complete.
enum class SideMenuState {
    CLOSED, OPEN, CLOSING;
    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

// Styles & Animations

val NavHeaderLogoStyle = CssStyle {
    base { Modifier.height(2.75.cssRem).display(DisplayStyle.Block) }
    Breakpoint.MD { Modifier.height(3.5.cssRem) }
}

val NavHeaderStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(1.cssRem)
}

val DropdownFadeInAnim = Keyframes {
    from { Modifier.opacity(0).translateY((-4).px) }
    to   { Modifier.opacity(1).translateY(0.px) }
}

val SideMenuSlideInAnim = Keyframes {
    from { Modifier.translateX(100.percent) }
    to   { Modifier }
}

val OverlayFadeInAnim = Keyframes {
    from { Modifier.backgroundColor(Colors.Transparent) }
    to   { Modifier.backgroundColor(Color.rgba(0, 0, 0, 160)) }
}

val OverlayFadeOutAnim = Keyframes {
    from { Modifier.backgroundColor(Color.rgba(0, 0, 0, 160)) }
    to   { Modifier.backgroundColor(Colors.Transparent) }
}

// Removes the blue flash on mobile tap
private val noTapHighlight = Modifier.attrsModifier { style { property("-webkit-tap-highlight-color", "transparent") } }

// Shared link modifiers to avoid repetition
private val dropdownItemMod get() = Modifier
    .color(Theme.DarkGreen.rgb)
    .classNames("fs-6", "px-3", "py-1", "dropdown-item")
    .then(noTapHighlight)

private val mobileItemMod get() = Modifier
    .fillMaxWidth()
    .color(Theme.DarkGreen.rgb)
    .classNames("fs-6", "py-2", "ps-3", "d-block", "text-start")
    .then(noTapHighlight)

// Buttons

@Composable
fun HamburgerButton(onClick: () -> Unit) {
    Div(attrs = Modifier.padding(0.6.cssRem).cursor(Cursor.Pointer).attrsModifier {
        attr("aria-label", "Open Menu")
        onClick { onClick() }
    }.toAttrs()) {
        FaBars(modifier = Modifier.color(Theme.DarkGreen.rgb), size = IconSize.SM)
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    Div(attrs = Modifier.padding(0.6.cssRem).cursor(Cursor.Pointer).attrsModifier {
        attr("aria-label", "Close Menu")
        onClick { onClick() }
    }.toAttrs()) {
        FaXmark(modifier = Modifier.color(Theme.DarkGreen.rgb), size = IconSize.SM)
    }
}

// Shared State Items

// Abbreviations shown in the nav dropdown (states with meaningful content/pages).
// Derived from StateData — link to the dedicated state page if one exists, otherwise /states.
private val navStateAbbrs = setOf("CA", "MI", "MN", "NV", "NJ", "NY", "OK", "OR", "WA")

// Builds the states dropdown list. useFullNames = true for mobile (shows "California"),
// false for desktop (shows "CA"). The first item is always a link to the full states page.
private fun stateItems(useFullNames: Boolean): List<NavDropdownItem> = buildList {
    add(NavDropdownItem(if (useFullNames) "View All States" else "VIEW MAP", "/states"))
    allStates
        .filter { it.abbr in navStateAbbrs }
        .forEach { state ->
            val label = if (useFullNames) state.name else state.abbr
            val path = state.statePage ?: "/states"
            add(NavDropdownItem(label, path))
        }
}

// Desktop Navbar

@Composable
fun NavDropdown(
    label: String,
    items: List<NavDropdownItem>,
    isOpen: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit
) {
    Div(attrs = Modifier.classNames("dropdown").attrsModifier { onClick { it.stopPropagation() } }.toAttrs()) {
        Link(
            path = "#",
            text = label.uppercase(),
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            modifier = NavLinkHoverStyle.toModifier()
                .classNames("fs-6", "p-2", "dropdown-toggle")
                .attrsModifier {
                    attr("aria-expanded", if (isOpen) "true" else "false")
                    onClick { it.preventDefault(); onToggle() }
                }
        )
        Ul(attrs = Modifier
            .classNames("dropdown-menu", "dropdown-menu-light", "mt-2")
            .then(if (isOpen) Modifier.classNames("show").animation(
                DropdownFadeInAnim.toAnimation(duration = 150.ms, timingFunction = AnimationTimingFunction.EaseOut)
            ) else Modifier)
            .toAttrs()
        ) {
            items.forEach { item ->
                Li {
                    if (item.isDivider) {
                        Hr(attrs = Modifier.classNames("dropdown-divider").toAttrs())
                    } else {
                        Link(
                            path = item.path,
                            text = item.label,
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            modifier = dropdownItemMod.attrsModifier { onClick { onClose() } }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun NavLink(path: String, text: String) {
    Link(
        path = path,
        text = text.uppercase(),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        modifier = NavLinkHoverStyle.toModifier().classNames("fs-6", "p-2")
    )
}

@Composable
private fun MenuItems() {
    var openDropdown by remember { mutableStateOf<String?>(null) }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { openDropdown = null }
        document.addEventListener("click", listener)
        onDispose { document.removeEventListener("click", listener) }
    }

    NavDropdown(
        label = "Our Services",
        items = listOf(
            NavDropdownItem("Rebates & Incentives", SitePage.Landing.OUR_SERVICES),
            NavDropdownItem("Lighting", SitePage.Landing.LED_GROW_LIGHT_REBATES),
            NavDropdownItem("Dehumidifier", SitePage.Landing.DEHUMIDIFIER_REBATES),
            NavDropdownItem("HVAC", SitePage.Landing.HVAC_REBATES),
            NavDropdownItem("Financing", SitePage.Landing.FINANCING),
        ),
        isOpen = openDropdown == "Services",
        onToggle = { openDropdown = if (openDropdown == "Services") null else "Services" },
        onClose = { openDropdown = null }
    )
    NavLink("/states", "Map")
//    NavDropdown(
//        label = "States",
//        items = stateItems(useFullNames = true),
//        isOpen = openDropdown == "States",
//        onToggle = { openDropdown = if (openDropdown == "States") null else "States" },
//        onClose = { openDropdown = null }
//    )
    NavDropdown(
        label = "More",
        items = listOf(
            NavDropdownItem("FAQ", "/faq"),
            NavDropdownItem("Blog", SitePage.Blog.route),
            NavDropdownItem("Articles", SitePage.Article.route),
            NavDropdownItem("Podcast", SitePage.Podcast.route),
            NavDropdownItem("Meet the Team", SitePage.MeetTheTeam.route),
            NavDropdownItem("Partners", SitePage.Partners.route),
            NavDropdownItem.Divider,
            //NavDropdownItem("DLC Qualified Rebates", SitePage.Landing.DLC_QUALIFIED_REBATES), // TODO REVIVE
            NavDropdownItem("Cannabis Rebates", SitePage.Landing.CANNABIS_REBATES),
        ),
        isOpen = openDropdown == "More",
        onToggle = { openDropdown = if (openDropdown == "More") null else "More" },
        onClose = { openDropdown = null }
    )
    NavLink("/contact-us", "Contact Us")
}

// Mobile Navbar

@Composable
private fun MobileNavAccordion(
    label: String,
    items: List<NavDropdownItem>,
    isOpen: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit
) {
    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.Start) {
        Row(
            modifier = NavLinkHoverStyle.toModifier().classNames("fs-6", "p-2").cursor(Cursor.Pointer)
                .attrsModifier { onClick { it.preventDefault(); onToggle() } },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(attrs = Modifier.color(Theme.DarkGreen.rgb).toAttrs()) { Text(label.uppercase()) }
            val chevronMod = Modifier.margin(left = 6.px).color(Theme.DarkGreen.rgb)
            if (isOpen) FaChevronUp(modifier = chevronMod, size = IconSize.SM)
            else         FaChevronDown(modifier = chevronMod, size = IconSize.SM)
        }
        if (isOpen) {
            Column(
                Modifier.fillMaxWidth().padding(bottom = 4.px)
                    .animation(DropdownFadeInAnim.toAnimation(duration = 150.ms, timingFunction = AnimationTimingFunction.EaseOut)),
                horizontalAlignment = Alignment.Start
            ) {
                items.forEach { item ->
                    if (item.isDivider) {
                        Hr(attrs = Modifier.classNames("my-1").fillMaxWidth().toAttrs())
                    } else {
                        Link(
                            path = item.path,
                            text = item.label,
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            modifier = mobileItemMod.attrsModifier { onClick { onClose() } }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MobileMenuItems() {
    var openSection by remember { mutableStateOf<String?>(null) }

    MobileNavAccordion(
        label = "Services",
        items = listOf(
            NavDropdownItem("Rebates & Incentives", SitePage.Landing.OUR_SERVICES),
            NavDropdownItem("Lighting", SitePage.Landing.LED_GROW_LIGHT_REBATES),
            NavDropdownItem("Dehumidifier", SitePage.Landing.DEHUMIDIFIER_REBATES),
            NavDropdownItem("HVAC", SitePage.Landing.HVAC_REBATES),
            NavDropdownItem("Financing", SitePage.Landing.FINANCING),
            NavDropdownItem.Divider,
            NavDropdownItem("DLC Qualified Rebates", SitePage.Landing.DLC_QUALIFIED_REBATES),
            NavDropdownItem("Cannabis Rebates", SitePage.Landing.CANNABIS_REBATES),
        ),
        isOpen = openSection == "Services",
        onToggle = { openSection = if (openSection == "Services") null else "Services" },
        onClose = { openSection = null }
    )
    MobileNavAccordion(
        label = "States",
        items = stateItems(useFullNames = true),
        isOpen = openSection == "States",
        onToggle = { openSection = if (openSection == "States") null else "States" },
        onClose = { openSection = null }
    )
    MobileNavAccordion(
        label = "More",
        items = listOf(
            NavDropdownItem("FAQ", "/faq"),
            NavDropdownItem("Blog", SitePage.Blog.route),
            NavDropdownItem("Articles", SitePage.Article.route),
            NavDropdownItem("Meet the Team", SitePage.MeetTheTeam.route),
            NavDropdownItem("Partners", SitePage.Partners.route),
        ),
        isOpen = openSection == "More",
        onToggle = { openSection = if (openSection == "More") null else "More" },
        onClose = { openSection = null }
    )
    NavLink("/contact-us", "Contact Us")
}

@Composable
fun NavHeader() {
    Nav(
        attrs = NavHeaderStyle.toModifier().wideCappedWidth()
            .classNames("d-flex", "flex-row", "align-items-center")
            .role("navigation").toAttrs()
    ) {
        Link(path = "/") {
            Image("/grlogocenter.webp", "Green Rebates Logo", modifier = NavHeaderLogoStyle.toModifier())
        }
        Spacer()
        Row(Modifier.gap(1.cssRem).displayIfAtLeast(Breakpoint.LG), verticalAlignment = Alignment.CenterVertically) {
            MenuItems()
        }
        Row(Modifier.fontSize(1.5.cssRem).gap(1.cssRem).displayUntil(Breakpoint.LG), verticalAlignment = Alignment.CenterVertically) {
            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }
            HamburgerButton(onClick = { menuState = SideMenuState.OPEN })
            if (menuState != SideMenuState.CLOSED) {
                SideMenu(
                    menuState,
                    close = { menuState = menuState.close() },
                    onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED }
                )
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Div(
        attrs = Modifier
            .position(Position.Fixed).top(0.px).left(0.px).fillMaxSize()
            .zIndex(4)
            .display(DisplayStyle.Flex).justifyContent(JustifyContent.FlexEnd)
            .onClick { close() }
            .animation(
                if (menuState == SideMenuState.OPEN)
                    OverlayFadeInAnim.toAnimation(duration = 200.ms, timingFunction = AnimationTimingFunction.EaseOut, fillMode = AnimationFillMode.Forwards)
                else
                    OverlayFadeOutAnim.toAnimation(duration = 200.ms, timingFunction = AnimationTimingFunction.EaseIn, fillMode = AnimationFillMode.Forwards)
            )
            .toAttrs()
    ) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column(
                Modifier
                    .fillMaxHeight()
                    .width(clamp(18.cssRem, 65.percent, 28.cssRem))
                    .padding(top = 3.5.cssRem, leftRight = 1.cssRem)
                    .backgroundColor(Colors.White)
                    .animation(SideMenuSlideInAnim.toAnimation(
                        duration = 200.ms,
                        timingFunction = AnimationTimingFunction.EaseInOut,
                        direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                        fillMode = AnimationFillMode.Forwards
                    ))
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.Start
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    CloseButton(onClick = { close() })
                }
                Column(Modifier.fillMaxWidth().gap(2.cssRem).fontSize(1.55.cssRem), horizontalAlignment = Alignment.Start) {
                    MobileMenuItems()
                }
                Spacer()
                SideMenuSocialsRow()
            }
        }
    }
}

private data class SocialLink(val url: String, val label: String, val icon: @Composable () -> Unit)

private val socialLinks = listOf(
    SocialLink("https://www.facebook.com/greenrebates/",                    "Read more on our Facebook page")  { FaFacebook(size = IconSize.LG) },
    SocialLink("https://www.linkedin.com/company/green-rebates",            "Read more on our LinkedIn page")  { FaLinkedin(size = IconSize.LG) },
    SocialLink("https://www.instagram.com/greenrebates/",                   "Read more on our Instagram page") { FaInstagram(size = IconSize.LG) },
    SocialLink("https://www.tiktok.com/@green.rebates",                     "Read more on our Tiktok page")    { FaTiktok(size = IconSize.LG) },
    SocialLink("https://www.youtube.com/channel/UCjREfEKvNsrM0wyNN_aQaEw", "See more on our YouTube channel") { FaYoutube(size = IconSize.LG) },
    SocialLink("https://x.com/greenrebates1",                               "Read more on our X/Twitter page") { FaXTwitter(size = IconSize.LG) },
)

@Composable
fun SideMenuSocialsRow() {
    P(attrs = Modifier.fillMaxWidth().textAlign(TextAlign.Center).color(Theme.DarkGreen.rgb).margin(0.px).fontSize(0.875.cssRem).toAttrs()) {
        Text("Follow Us")
    }
    Row(
        modifier = Modifier.fillMaxWidth().padding(leftRight = 1.cssRem, bottom = 2.cssRem, top = 0.25.cssRem).fontSize(0.875.cssRem),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalAlignment = Alignment.CenterVertically
    ) {
        socialLinks.forEach { social ->
            Link(
                path = social.url,
                openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                modifier = Modifier.color(Theme.DarkGreen.rgb).attrsModifier { attr("aria-label", social.label) }
            ) {
                social.icon()
            }
        }
    }
}

val ContactHeaderStyle = CssStyle.base {
    Modifier.fillMaxWidth().backgroundColor(Theme.MediumGreen.rgb)
        .position(Position.Relative).zIndex(5)
}

@Composable
fun ContactHeader() {
    Row(
        modifier = ContactHeaderStyle.toModifier(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        Row(
            modifier = Modifier.width(min(1140.px, 100.percent)).classNames("h-100", "text-white", "py-2", "px-4"),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Link(
                path = "/contact-us",
                variant = UndecoratedLinkVariant,
                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE
            ) {
                H3(attrs = NavContactHeaderTextStyle.toModifier().toAttrs()) {
                    FaEnvelope(modifier = Modifier.margin(right = 6.px))
                    Text("CONTACT US")
                }
            }
            Spacer()
            Link(
                path = "tel:800-208-1455",
                modifier = NavContactHeaderTextStyle.toModifier()
            ) {
                FaPhone(modifier = Modifier.margin(right = 6.px))
                Text("800-208-1455")
            }
        }
    }
}
